// client/src/dnd/emitter_tool/EmitterTool.js
import React, { useMemo, useRef, useState, useEffect } from "react";
import EffectsSidebarDefault, { EffectsSidebar as EffectsSidebarNamed } from "./components/EffectsSidebar";
import EnemyToken from "./components/EnemyToken.js";
import GridCanvas from "./components/stages/GridCanvas.js";
import WallsStage from "./components/stages/WallsStage.js";
import TokenStage from "./components/stages/TokenStage.js";
import EmitterStage from "./components/stages/EmitterStage.js";
import useAtlas from "./hooks/useAtlas.js";
import useEmitterLayers from "./hooks/useEmitterLayers.js";
import useTargetingHost from "./hooks/useTargetingHost.js";
import exportPreset from "./utils/exportPreset.js";
import { clamp01 } from "./utils/clamp.js";
import useModeHotkeys from "./hooks/useModeHotkeys.js";

const EffectsSidebar =
  typeof EffectsSidebarDefault === "function" ? EffectsSidebarDefault : EffectsSidebarNamed;

const UI_WALLS_KEY       = "EmitterTool:UI:wallsEnabled";
const UI_TOKEN_KEY       = "EmitterTool:UI:tokenEnabled";
const UI_ENEMY_TOKEN_KEY = "EmitterTool:UI:enemyTokenEnabled";
const UI_GRID_KEY        = "EmitterTool:UI:showGrid";
const UI_SHOW_SHAPE_KEY  = "EmitterTool:UI:showShape";
const UI_HIDE_FOG_KEY    = "EmitterTool:UI:hideFog";

const readBool = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    if (v === "1") return true;
    if (v === "0") return false;
    return !!fallback;
  } catch {
    return !!fallback;
  }
};
const writeBool = (key, val) => {
  try { localStorage.setItem(key, val ? "1" : "0"); } catch {}
};

const DEFAULT_SHAPE_SETTINGS = {
  radius: 1,
  width: 2,
  height: 2,
  rotationDeg: 0,
  angleDeg: 0,
  spreadDeg: 30,
  minDist: 0,
  maxDist: 3,
};

export default function EmitterTool() {
  const [effectName, setEffectName] = useState("Emitter Tool");
  const panelTitle = effectName;

  const effects = useMemo(() => ({}), []);
  const [effectKey, setEffectKey] = useState("basic");

  const { atlas, selectedSprite, setSelectedSprite } = useAtlas();

  const [wallsEnabled, setWallsEnabled]   = useState(() => readBool(UI_WALLS_KEY,       true));
  const [tokenEnabled, setTokenEnabled]   = useState(() => readBool(UI_TOKEN_KEY,       true));
  const [enemyEnabled, setEnemyEnabled]   = useState(() => readBool(UI_ENEMY_TOKEN_KEY, true));
  const [showGrid, setShowGrid]           = useState(() => readBool(UI_GRID_KEY,        true));
  const [showShape, setShowShape]         = useState(() => readBool(UI_SHOW_SHAPE_KEY,  true));

  useEffect(() => { writeBool(UI_WALLS_KEY,       wallsEnabled); }, [wallsEnabled]);
  useEffect(() => { writeBool(UI_TOKEN_KEY,       tokenEnabled); }, [tokenEnabled]);
  useEffect(() => { writeBool(UI_ENEMY_TOKEN_KEY, enemyEnabled); }, [enemyEnabled]);
  useEffect(() => { writeBool(UI_GRID_KEY,        showGrid);     }, [showGrid]);
  useEffect(() => { writeBool(UI_SHOW_SHAPE_KEY,  showShape);    }, [showShape]);

  const [pxPerMeter, setPxPerMeter] = useState(80);

  const [emitterShape, setEmitterShape] = useState("dot");
  const [shapeSettings, setShapeSettings] = useState({ ...DEFAULT_SHAPE_SETTINGS });

  const [effectSettings, setEffectSettings] = useState(() => {
    const fogHiddenFromLS = readBool(UI_HIDE_FOG_KEY, false);
    return { fogHidden: fogHiddenFromLS, fogAlpha: 1 };
  });
  const fogHidden = !!effectSettings?.fogHidden;
  const fogAlpha = clamp01(
    Number.isFinite(effectSettings?.fogAlpha) ? effectSettings.fogAlpha : 1
  );

  useEffect(() => {
    if (typeof effectSettings?.fogHidden === "boolean") {
      writeBool(UI_HIDE_FOG_KEY, !!effectSettings.fogHidden);
    }
  }, [effectSettings?.fogHidden]);

  const [originPx, setOriginPx] = useState({ x: 180, y: 180 });
  const [originReady, setOriginReady] = useState(false);

  const {
    layers,
    setLayers,
    activeLayerIndex,
    setActiveLayerIndex,
    onAddChildFromParent,
    onRemoveLayer,
    onUpdateLayer,
    onReorderLayers,
  } = useEmitterLayers({
    effectKey,
    selectedSprite,
    effectSettings,
    emitterShape,
    shapeSettings,
  });

  const stableLayers = useMemo(() => layers, [layers]);

  const [mousePx, setMousePx] = useState({ x: 0, y: 0 });
  const [guidePx, setGuidePx] = useState(null);

  const fileInputRef = useRef(null);
  const onImport = () => fileInputRef.current?.click();
  const onExport = () =>
    exportPreset(
      effectName,
      effectKey,
      pxPerMeter,
      showGrid,
      {
        selectedSprite,
        effectSettings,
        emitterShape,
        shapeSettings,
        infinite: false,
        infiniteRate: 30,
      },
      layers
    );

  const applyPresetText = () => {};

  const handleTokenPos = (pos) => {
    setOriginPx(pos);
    setOriginReady(true);
  };

  const { hostRef: targetingHostRef } = useTargetingHost({
    getOrigin: () =>
      originReady ? { x: originPx.x, y: originPx.y } : { x: 0, y: 0 },
    setMousePx,
    setGuidePx,
  });

  useModeHotkeys({ enable: true, pointRangeM: 9, timedSeconds: 3 });

  useEffect(() => {
    const el = targetingHostRef?.current;
    if (!el) return;

    function publishSize(w, h) {
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:UI:ContainerSize", {
            detail: { size: [Math.round(w), Math.round(h)] },
          })
        );
      } catch {}
    }

    const initialRect = el.getBoundingClientRect?.();
    if (initialRect && initialRect.width && initialRect.height) {
      publishSize(initialRect.width, initialRect.height);
    }

    let ro;
    try {
      ro = new ResizeObserver((entries) => {
        const cr = entries[0]?.contentRect;
        if (!cr) return;
        publishSize(cr.width, cr.height);
      });
      ro.observe(el);
    } catch {
      const onWin = () => {
        const r = el.getBoundingClientRect?.();
        if (r) publishSize(r.width, r.height);
      };
      window.addEventListener("resize", onWin);
      return () => window.removeEventListener("resize", onWin);
    }

    return () => {
      try { ro?.disconnect?.(); } catch {}
    };
  }, [targetingHostRef]);

  const emitStageEvent = (name, detail) => {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    } catch {}
  };

  const onStageMouseDown = (e) => {
    try {
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation?.();
    } catch {}
    const rect = e.currentTarget.getBoundingClientRect?.() || { left: 0, top: 0 };
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    emitStageEvent("EmitterTool:UI:StageMouseDown", { x, y, button: e.button });
  };

  const onStageClick = (e) => {
    try {
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation?.();
    } catch {}
    const rect = e.currentTarget.getBoundingClientRect?.() || { left: 0, top: 0 };
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    emitStageEvent("EmitterTool:UI:StageClick", { x, y, button: e.button });
  };

  const BG_URL = "/assets/emitter/background.jpg";

  const delayTimersRef = useRef(new Map());
  useEffect(() => {
    const timers = delayTimersRef.current;
    const onRule = (ev) => {
      const d = ev?.detail || {};
      const id = d?.id;
      if (!id) return;
      if (timers.has(id)) {
        try { clearTimeout(timers.get(id)); } catch {}
        timers.delete(id);
      }
      if ((d.startMode === "delay") && (d.enabled !== false)) {
        const ms = Number(d.delayMs) || 0;
        if (ms > 0) {
          const t = setTimeout(() => {
            try {
              window.dispatchEvent(
                new CustomEvent("EmitterTool:SubEmitterRule", {
                  detail: { ...d, startMode: "immediate", delayMs: 0, reason: "delayedFire" },
                })
              );
            } catch {}
            timers.delete(id);
          }, ms);
          timers.set(id, t);
        } else {
          try {
            window.dispatchEvent(
              new CustomEvent("EmitterTool:SubEmitterRule", {
                detail: { ...d, startMode: "immediate", delayMs: 0, reason: "delayZeroImmediate" },
              })
            );
          } catch {}
        }
      }
    };
    try { window.addEventListener("EmitterTool:SubEmitterRule", onRule); } catch {}
    return () => {
      try { window.removeEventListener("EmitterTool:SubEmitterRule", onRule); } catch {}
      timers.forEach((t) => { try { clearTimeout(t); } catch {} });
      timers.clear();
    };
  }, []);

  return (
    <div style={{ display: "flex", height: "100vh", width: "100%" }}>
      <input ref={fileInputRef} type="file" accept=".js,.json" style={{ display: "none" }} />

      <div
        data-et-ui="sidebar"
        onMouseDown={(e) => {
          try { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); } catch {}
        }}
        onClick={(e) => {
          try { e.stopPropagation(); e.nativeEvent?.stopImmediatePropagation?.(); } catch {}
        }}
        style={{ position: "relative", zIndex: 10000 }}
      >
        <EffectsSidebar
          effectName={effectName}
          setEffectName={setEffectName}
          onExport={onExport}
          onImport={onImport}
          applyPresetText={applyPresetText}
          effects={effects}
          effectKey={effectKey}
          setEffectKey={setEffectKey}
          atlas={atlas}
          selectedSprite={selectedSprite}
          setSelectedSprite={setSelectedSprite}
          wallsEnabled={wallsEnabled}
          setWallsEnabled={setWallsEnabled}
          tokenEnabled={tokenEnabled}
          setTokenEnabled={setTokenEnabled}
          enemyEnabled={enemyEnabled}
          setEnemyEnabled={setEnemyEnabled}
          effectSettings={effectSettings}
          setEffectSettings={setEffectSettings}
          panelTitle={panelTitle}
          emitterShape={emitterShape}
          setEmitterShape={setEmitterShape}
          shapeSettings={shapeSettings}
          setShapeSettings={setShapeSettings}
          showShape={showShape}
          setShowShape={setShowShape}
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          pxPerMeter={pxPerMeter}
          setPxPerMeter={setPxPerMeter}
          layers={layers}
          setLayers={setLayers}
          activeLayerIndex={activeLayerIndex}
          setActiveLayerIndex={setActiveLayerIndex}
          onAddChildFromParent={onAddChildFromParent}
          onRemoveLayer={onRemoveLayer}
          onUpdateLayer={onUpdateLayer}
          onReorderLayers={onReorderLayers}
          onToggleFogHidden={() =>
            setEffectSettings((cur) => ({
              ...(cur || {}),
              fogHidden: !cur?.fogHidden,
            }))
          }
        />
      </div>

      <div
        ref={targetingHostRef}
        data-emitter-stage="1"
        onMouseDown={onStageMouseDown}
        onClick={onStageClick}
        style={{ position: "relative", flex: 1, background: "#0b0f17" }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${BG_URL})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />

        {originReady && (
          <EmitterStage
            atlas={atlas}
            pxPerMeter={pxPerMeter}
            effectName={effectName}
            effectKey={effectKey}
            selectedSprite={selectedSprite}
            effectSettings={effectSettings}
            emitterShape={emitterShape}
            shapeSettings={shapeSettings}
            layers={stableLayers}
            originPx={originPx}
            originReady={originReady}
            showShape={showShape}
            mousePx={mousePx}
            guidePx={guidePx}
            fogHidden={fogHidden}
            fogAlpha={fogAlpha}
          />
        )}

        <GridCanvas show={showGrid} pxPerMeter={pxPerMeter} />

        {tokenEnabled && (
          <TokenStage
            enabled={true}
            startPos={{ x: originPx.x, y: originPx.y }}
            onPositionChange={handleTokenPos}
          />
        )}

        {enemyEnabled && <EnemyToken />}

        {wallsEnabled && <WallsStage enabled={true} />}
      </div>
    </div>
  );
}
